/**
 * speechSymbols — glyph → spoken words for the TTS pre-clean.
 *
 * Split out of useElevenLabs.js so it is testable: the hook imports React and
 * uses Vite-style extensionless imports, so `node --test` cannot load it
 * (same constraint that produced ChatInterface/mermaidNormalize.js).
 *
 * WHY THIS IS A TABLE AND NOT A CHAIN OF .replace() CALLS: the previous
 * hand-written chain covered exactly the nine glyphs `latexNormalize` could
 * emit at the time (≈ ≠ ≥ ≤ ± ∞ π × ÷ ·). When the normalizer's symbol set
 * widened on 2026-08-31 to cover arrows and Greek, every new glyph reached
 * ElevenLabs as an UNSPOKEN character — silently dropped mid-sentence, which
 * is worse than the raw markup it replaced. `speechSymbols.parity.test.js`
 * now fails if the normalizer can emit a glyph this table has no word for.
 */

// glyph → what a person would say reading it aloud. Surrounding spaces are
// deliberate: these substitute INTO running prose.
export const SPEECH_SYMBOLS = {
  // arrows — overwhelmingly flow arrows in practice ("VSL → booking page")
  '→': ' to ',
  '⟶': ' to ',
  '←': ' from ',
  '⟵': ' from ',
  '↔': ' to and from ',
  '⇒': ' implies ',
  '⇐': ' is implied by ',
  '⇔': ' if and only if ',
  '↦': ' maps to ',
  '↑': ' up ',
  '↓': ' down ',
  // arithmetic / relational (the original nine, folded in — one table now)
  '×': ' times ',
  '÷': ' divided by ',
  '·': ' times ',
  '≈': ' approximately ',
  '≠': ' not equal to ',
  '≥': ' greater than or equal to ',
  '≤': ' less than or equal to ',
  '±': ' plus or minus ',
  '∓': ' minus or plus ',
  '≡': ' equivalent to ',
  '∼': ' similar to ',
  '≃': ' approximately equal to ',
  '∝': ' proportional to ',
  '≪': ' much less than ',
  '≫': ' much greater than ',
  // set / logic
  '∈': ' in ',
  '∉': ' not in ',
  '⊂': ' subset of ',
  '⊆': ' subset of or equal to ',
  '⊃': ' superset of ',
  '⊇': ' superset of or equal to ',
  '∪': ' union ',
  '∩': ' intersect ',
  '∀': ' for all ',
  '∃': ' there exists ',
  '∅': ' the empty set ',
  '∴': ' therefore ',
  '∵': ' because ',
  // analysis
  '∞': ' infinity ',
  '∂': ' partial ',
  '∇': ' del ',
  '∑': ' the sum of ',
  '∏': ' the product of ',
  '∫': ' the integral of ',
  'ℓ': ' ell ',
  'ℏ': ' h bar ',
  // punctuation / misc
  '√': ' square root of ',
  '°': ' degrees ',
  '∘': ' composed with ',
  '∗': ' star ',
  '⋆': ' star ',
  '∠': ' angle ',
  '⊥': ' perpendicular to ',
  '∥': ' parallel to ',
  '•': ' ',
  // greek — spelled out; TTS reads the raw letters as foreign-language text
  'α': ' alpha ', 'β': ' beta ', 'γ': ' gamma ', 'δ': ' delta ',
  'ε': ' epsilon ', 'ϵ': ' epsilon ', 'ζ': ' zeta ', 'η': ' eta ', 'θ': ' theta ',
  'ϑ': ' theta ', 'ι': ' iota ', 'κ': ' kappa ', 'λ': ' lambda ',
  'μ': ' mu ', 'ν': ' nu ', 'ξ': ' xi ', 'π': ' pi ', 'ρ': ' rho ',
  'σ': ' sigma ', 'τ': ' tau ', 'υ': ' upsilon ', 'φ': ' phi ', 'ϕ': ' phi ',
  'χ': ' chi ', 'ψ': ' psi ', 'ω': ' omega ',
  'Γ': ' capital gamma ', 'Δ': ' delta ', 'Θ': ' capital theta ',
  'Λ': ' capital lambda ', 'Ξ': ' capital xi ', 'Π': ' capital pi ',
  'Σ': ' the sum of ', 'Υ': ' capital upsilon ', 'Φ': ' capital phi ',
  'Ψ': ' capital psi ', 'Ω': ' omega ',
};

// Glyphs that legitimately need no expansion — a TTS engine says them
// correctly, or they are punctuation that should simply be read as written.
// Listed EXPLICITLY so the parity test can tell "handled" from "forgotten".
// `√` is NOT here: useElevenLabs has its own `√\s*(\d+)` rule that swallows a
// NUMERIC operand ("√52" → "square root of 52"), but a bare "√x" fell through
// it and was dropped silently. The table entry catches whatever that rule does
// not, and the digit rule still runs first.
// `…`/`⋯` are here rather than in the table on purpose: U+2026 reaches TTS from
// ordinary model prose far more often than from `\ldots`, and every engine
// already reads it as a pause — which is the correct reading in BOTH cases.
// Expanding it to " and so on " turned "Well… let me think." into
// "Well and so on let me think." (review finding, 2026-08-31).
export const SPEECH_PASSTHROUGH = new Set(['…', '⋯']);

const SPEECH_SYMBOL_RE = new RegExp(
  `[${Object.keys(SPEECH_SYMBOLS).join('').replace(/[\]\\^-]/g, '\\$&')}]`,
  'g',
);

/** Replace every known glyph with its spoken form, in one pass. */
export function expandSymbolsForSpeech(text) {
  if (typeof text !== 'string' || text.length === 0) return text;
  return text.replace(SPEECH_SYMBOL_RE, (g) => SPEECH_SYMBOLS[g]);
}
